import React from "react";
import { Shield, ExternalLink } from "lucide-react";

export default function Privacy() {
  const [theme, setTheme] = React.useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  React.useEffect(() => {
    const handleStorage = () => {
      setTheme(localStorage.getItem('theme') || 'dark');
    };
    window.addEventListener('storage', handleStorage);
    
    const handleThemeChange = () => {
      setTheme(localStorage.getItem('theme') || 'dark');
    };
    window.addEventListener('themechange', handleThemeChange);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('themechange', handleThemeChange);
    };
  }, []);

  return (
    <div className={`min-h-screen ${
      theme === 'dark' ? 'bg-gray-900' : 'bg-white'
    }`}>
      {/* Header */}
      <div className="bg-gradient-to-br from-orange-500 via-coral-500 to-pink-500 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-xl text-white/90">Effective as of November 8, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className={`prose prose-lg max-w-none ${
          theme === 'dark' ? 'prose-invert' : ''
        }`}>
          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            This privacy policy applies to the <strong>Sunscreened</strong> app (hereby referred to as "Application") 
            for mobile devices that was created by Ammar Omar (hereby referred to as "Service Provider") as a Free service. 
            This service is intended for use "AS IS".
          </p>

          <h2 className={`text-2xl font-bold mt-8 mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Information Collection and Use
          </h2>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            The Application collects information when you download and use it. This information may include information such as
          </p>

          <ul className={`list-disc list-inside space-y-2 mb-4 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <li>Your device's Internet Protocol address (e.g. IP address)</li>
            <li>The pages of the Application that you visit, the time and date of your visit, the time spent on those pages</li>
            <li>The time spent on the Application</li>
            <li>The operating system you use on your mobile device</li>
          </ul>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            The Application does not gather precise information about the location of your mobile device.
          </p>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            The Application collects your device's location, which helps the Service Provider determine your approximate geographical location and make use of in below ways:
          </p>

          <ul className={`list-disc list-inside space-y-2 mb-4 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <li><strong>Geolocation Services:</strong> The Service Provider utilizes location data to provide features such as personalized content, relevant recommendations, and location-based services.</li>
            <li><strong>Analytics and Improvements:</strong> Aggregated and anonymized location data helps the Service Provider to analyze user behavior, identify trends, and improve the overall performance and functionality of the Application.</li>
            <li><strong>Third-Party Services:</strong> Periodically, the Service Provider may transmit anonymized location data to external services. These services assist them in enhancing the Application and optimizing their offerings.</li>
          </ul>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            The Service Provider may use the information you provided to contact you from time to time to provide you with important information, required notices and marketing promotions.
          </p>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            For a better experience, while using the Application, the Service Provider may require you to provide us with certain personally identifiable information, including but not limited to Email, height, weight, skin tone, user id, skin type, age, sex, skin sensitivity, weather or not they take supplemental vitamin d,. The information that the Service Provider request will be retained by them and used as described in this privacy policy.
          </p>

          <h2 className={`text-2xl font-bold mt-8 mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Third Party Access
          </h2>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            Only aggregated, anonymized data is periodically transmitted to external services to aid the Service Provider in improving the Application and their service. The Service Provider may share your information with third parties in the ways that are described in this privacy statement.
          </p>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            Please note that the Application utilizes third-party services that have their own Privacy Policy about handling data. Below are the links to the Privacy Policy of the third-party service providers used by the Application:
          </p>

          <ul className={`list-disc list-inside space-y-2 mb-4 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <li>
              <a 
                href="https://firebase.google.com/support/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-orange-600 hover:text-orange-700 inline-flex items-center gap-1"
              >
                Google Analytics for Firebase
                <ExternalLink className="w-3 h-3" />
              </a>
            </li>
            <li>
              <a 
                href="https://firebase.google.com/support/privacy/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-orange-600 hover:text-orange-700 inline-flex items-center gap-1"
              >
                Firebase Crashlytics
                <ExternalLink className="w-3 h-3" />
              </a>
            </li>
            <li>
              <a 
                href="https://expo.io/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-orange-600 hover:text-orange-700 inline-flex items-center gap-1"
              >
                Expo
                <ExternalLink className="w-3 h-3" />
              </a>
            </li>
          </ul>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            The Service Provider may disclose User Provided and Automatically Collected Information:
          </p>

          <ul className={`list-disc list-inside space-y-2 mb-4 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <li>as required by law, such as to comply with a subpoena, or similar legal process;</li>
            <li>when they believe in good faith that disclosure is necessary to protect their rights, protect your safety or the safety of others, investigate fraud, or respond to a government request;</li>
            <li>with their trusted services providers who work on their behalf, do not have an independent use of the information we disclose to them, and have agreed to adhere to the rules set forth in this privacy statement.</li>
          </ul>

          <h2 className={`text-2xl font-bold mt-8 mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Opt-Out Rights
          </h2>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            You can stop all collection of information by the Application easily by uninstalling it. You may use the standard uninstall processes as may be available as part of your mobile device or via the mobile application marketplace or network.
          </p>

          <h2 className={`text-2xl font-bold mt-8 mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Data Retention Policy
          </h2>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            The Service Provider will retain User Provided data for as long as you use the Application and for a reasonable time thereafter. If you'd like them to delete User Provided Data that you have provided via the Application, please contact them at <a href="mailto:sunscreened26@gmail.com" className="text-orange-600 hover:text-orange-700">sunscreened26@gmail.com</a> and they will respond in a reasonable time.
          </p>

          <h2 className={`text-2xl font-bold mt-8 mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Children
          </h2>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            The Service Provider does not use the Application to knowingly solicit data from or market to children under the age of 13.
          </p>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            The Application does not address anyone under the age of 13. The Service Provider does not knowingly collect personally identifiable information from children under 13 years of age. In the case the Service Provider discover that a child under 13 has provided personal information, the Service Provider will immediately delete this from their servers. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact the Service Provider (<a href="mailto:sunscreened26@gmail.com" className="text-orange-600 hover:text-orange-700">sunscreened26@gmail.com</a>) so that they will be able to take the necessary actions.
          </p>

          <h2 className={`text-2xl font-bold mt-8 mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Security
          </h2>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            The Service Provider is concerned about safeguarding the confidentiality of your information. The Service Provider provides physical, electronic, and procedural safeguards to protect information the Service Provider processes and maintains.
          </p>

          <h2 className={`text-2xl font-bold mt-8 mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Changes
          </h2>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            This Privacy Policy may be updated from time to time for any reason. The Service Provider will notify you of any changes to the Privacy Policy by updating this page with the new Privacy Policy. You are advised to consult this Privacy Policy regularly for any changes, as continued use is deemed approval of all changes.
          </p>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            This privacy policy is effective as of <strong>2025-11-08</strong>
          </p>

          <h2 className={`text-2xl font-bold mt-8 mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Your Consent
          </h2>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            By using the Application, you are consenting to the processing of your information as set forth in this Privacy Policy now and as amended by us.
          </p>

          <h2 className={`text-2xl font-bold mt-8 mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Contact Us
          </h2>

          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
            If you have any questions regarding privacy while using the Application, or have questions about the practices, please contact the Service Provider via email at <a href="mailto:sunscreened26@gmail.com" className="text-orange-600 hover:text-orange-700">sunscreened26@gmail.com</a>.
          </p>

          <hr className={`my-8 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`} />

          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            This privacy policy page was generated by{' '}
            <a 
              href="https://app-privacy-policy-generator.nisrulz.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-orange-600 hover:text-orange-700"
            >
              App Privacy Policy Generator
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}











